<template>
  <div class="rdb-page">

    <!-- ─── Hero Header ─── -->
    <div class="rdb-hero" :class="isOnline ? 'rdb-hero--online' : 'rdb-hero--offline'">
      <div class="rdb-hero__inner">
        <div class="rdb-hero__info">
          <div class="rdb-hero__greeting">สวัสดี 👋</div>
          <div class="rdb-hero__name">{{ riderName }}</div>
        </div>
        <button class="rdb-status-toggle" @click="toggleOnlineStatus" :class="isOnline ? 'rdb-status-toggle--on' : 'rdb-status-toggle--off'">
          <span class="rdb-status-toggle__dot"></span>
          {{ isOnline ? 'ออนไลน์' : 'ออฟไลน์' }}
        </button>
      </div>

      <!-- Stats Row inside hero -->
      <div class="rdb-hero__stats">
        <div class="rdb-hero__stat">
          <div class="rdb-hero__stat-val">{{ stats.todayDeliveries }}</div>
          <div class="rdb-hero__stat-lbl">ส่งวันนี้</div>
        </div>
        <div class="rdb-hero__stat-divider"></div>
        <div class="rdb-hero__stat">
          <div class="rdb-hero__stat-val">฿{{ formatNumber(stats.todayEarnings) }}</div>
          <div class="rdb-hero__stat-lbl">รายได้วันนี้</div>
        </div>
        <div class="rdb-hero__stat-divider"></div>
        <div class="rdb-hero__stat" @click="$router.push({ name: 'rider_wallet' })">
          <div class="rdb-hero__stat-val">฿{{ formatNumber(stats.balance) }}</div>
          <div class="rdb-hero__stat-lbl">กระเป๋าเงิน 💰</div>
        </div>
      </div>
    </div>

    <!-- ─── Body ─── -->
    <div class="rdb-body">

      <!-- ─── Active Job Banner ─── -->
      <div
        v-if="currentDeliveries.length > 0"
        class="rdb-active-banner"
        @click="viewDetails(currentDeliveries[0])"
      >
        <div class="rdb-active-banner__left">
          <div class="rdb-active-banner__pulse">
            <v-icon size="22" color="white">mdi-moped</v-icon>
          </div>
          <div class="rdb-active-banner__text">
            <div class="rdb-active-banner__title">มีงานที่กำลังดำเนินการ!</div>
            <div class="rdb-active-banner__sub">#{{ currentDeliveries[0].orderNumber }} · {{ getStatusText(currentDeliveries[0].status, currentDeliveries[0].jobType) }}</div>
          </div>
        </div>
        <v-icon color="white" size="20">mdi-chevron-right</v-icon>
      </div>

      <!-- ─── เรียกไรเดอร์ ─── -->
      <div v-if="isOnline && availableTravelJobs.length > 0" class="rdb-section">
        <div class="rdb-category-header rdb-category-header--travel">
          <div class="rdb-category-header__icon">
            <v-icon size="20" color="white">mdi-car</v-icon>
          </div>
          <div class="rdb-category-header__text">
            <div class="rdb-category-header__title">เรียกไรเดอร์</div>
            <div class="rdb-category-header__sub">เรียกรถ / รับ-ส่งผู้โดยสาร</div>
          </div>
          <div class="rdb-category-header__count rdb-category-header__count--travel">{{ availableTravelJobs.length }} งาน</div>
        </div>

        <div
          v-for="job in availableTravelJobs"
          :key="'job-'+job.id"
          class="rdb-job-card rdb-job-card--travel"
        >
          <!-- ── Header: order num + name ── -->
          <div class="rdb-job-card__header rdb-job-card__header--travel">
            <span class="rdb-job-card__num">#{{ job.orderNumber }}</span>
            <v-spacer></v-spacer>
            <div class="rdb-job-source-badge rdb-job-source-badge--user">
              <v-icon size="11" color="inherit" class="mr-1">mdi-account</v-icon>
              {{ job.shopName }}
            </div>
            <div class="rdb-job-card__time-ago" v-if="job.timeAgo">
              <v-icon size="11" color="#9CA3AF" class="mr-1">mdi-clock-outline</v-icon>
              {{ job.timeAgo }}ที่แล้ว
            </div>
          </div>

          <!-- ── Route visual ── -->
          <div class="rdb-job-card__body">
            <!-- Pickup row -->
            <div class="rdb-job-route-row">
              <div class="rdb-job-route-indicator">
                <div class="rdb-job-route-dot rdb-job-route-dot--pickup"></div>
                <div class="rdb-job-route-connector"></div>
              </div>
              <div class="rdb-job-route-info">
                <div class="rdb-job-route-label">
                  {{ job.jobType === 'travel' ? 'จุดรับผู้โดยสาร' : 'รับสินค้า' }}
                  <span class="rdb-job-dist-chip rdb-job-dist-chip--near" v-if="getJobPickupDistance(job)">
                    <v-icon size="10" color="inherit">mdi-navigation</v-icon>
                    {{ formatDistance(getJobPickupDistance(job)) }} จากคุณ
                  </span>
                </div>
                <div class="rdb-job-route-name">{{ job.shopName }}</div>
                <div class="rdb-job-route-addr">{{ job.pickupAddress }}</div>
                <div v-if="job.pickupDetail" class="rdb-job-route-note">
                  <v-icon size="11" color="#16a34a" class="mr-1">mdi-note-text-outline</v-icon>
                  {{ job.pickupDetail }}
                </div>
              </div>
            </div>

            <!-- Drop row -->
            <div class="rdb-job-route-row">
              <div class="rdb-job-route-indicator">
                <div class="rdb-job-route-dot rdb-job-route-dot--drop"></div>
              </div>
              <div class="rdb-job-route-info">
                <div class="rdb-job-route-label">
                  {{ job.jobType === 'travel' ? 'จุดส่งผู้โดยสาร' : 'ส่งที่' }}
                  <span class="rdb-job-dist-chip rdb-job-dist-chip--deliver" v-if="getJobDeliveryDistance(job)">
                    <v-icon size="10" color="inherit">mdi-map-marker-distance</v-icon>
                    {{ formatDistance(getJobDeliveryDistance(job)) }} ระยะเดินทาง
                  </span>
                </div>
                <div class="rdb-job-route-name">{{ job.customerArea || 'ไม่ระบุพื้นที่' }}</div>
                <div v-if="job.deliveryDetail" class="rdb-job-route-note">
                  <v-icon size="11" color="#dc2626" class="mr-1">mdi-note-text-outline</v-icon>
                  {{ job.deliveryDetail }}
                </div>
              </div>
            </div>

            <!-- Rider note -->
            <div v-if="job.note" class="rdb-job-rider-note">
              <v-icon size="13" color="#D97706" class="mr-1">mdi-message-text-outline</v-icon>
              <span>{{ job.note }}</span>
            </div>

            <!-- Add-ons -->
            <div v-if="job.selectedAddons && job.selectedAddons.length > 0" class="rdb-job-addons">
              <div class="rdb-job-addons__title">
                <v-icon size="12" color="#7C3AED" class="mr-1">mdi-star-plus-outline</v-icon>
                Add-On ที่ลูกค้าเลือก
              </div>
              <div v-for="addon in job.selectedAddons" :key="addon.id" class="rdb-job-addon-row">
                <span class="rdb-job-addon-name">{{ addon.name }}</span>
                <span class="rdb-job-addon-price">+฿{{ addon.price.toLocaleString('th-TH') }}</span>
              </div>
            </div>

            <!-- Route summary strip -->
            <div class="rdb-job-summary-strip">
              <div class="rdb-job-summary-item" v-if="getJobPickupDistance(job)">
                <v-icon size="13" color="#3B82F6">mdi-navigation</v-icon>
                <span>{{ formatDistance(getJobPickupDistance(job)) }} ถึงจุดรับ</span>
              </div>
              <div class="rdb-job-summary-item" v-if="getJobDeliveryDistance(job)">
                <v-icon size="13" color="#F4811F">mdi-map-marker-distance</v-icon>
                <span>{{ formatDistance(getJobDeliveryDistance(job)) }} ระยะเดินทาง</span>
              </div>
              <div class="rdb-job-summary-item" v-if="job.itemCount">
                <v-icon size="13" color="#6B7280">mdi-package-variant</v-icon>
                <span>{{ job.itemCount }} รายการ</span>
              </div>
              <div class="rdb-job-summary-item" v-if="getEstimatedTime(job)">
                <v-icon size="13" color="#6B7280">mdi-timer-outline</v-icon>
                <span>ใช้เวลา ~{{ getEstimatedTime(job) }} นาที</span>
              </div>
            </div>
          </div>

          <!-- ── Footer: fee + accept ── -->
          <div class="rdb-job-card__footer">
            <div>
              <div class="rdb-job-card__fee">฿{{ formatNumber(job.deliveryFee) }}</div>
              <div v-if="job.addonTotal > 0" class="rdb-job-fee-addon">
                รวม Add-On +฿{{ formatNumber(job.addonTotal) }}
              </div>
              <div class="rdb-job-fee-per-km" v-if="getTotalDistance(job) && job.deliveryFee">
                ฿{{ getFeePerKm(job) }}/กม.
              </div>
            </div>
            <button
              class="rdb-accept-btn"
              @click.stop="acceptJob(job)"
              :disabled="currentDeliveries.length > 0"
            >
              <v-icon size="16" color="white" class="mr-1">mdi-check-circle</v-icon>
              รับงานนี้
            </button>
          </div>
        </div>
      </div>

      <!-- ─── ตลาดชุมชน ─── -->
      <div v-if="isOnline && availableOrderJobs.length > 0" class="rdb-section">
        <div class="rdb-category-header rdb-category-header--order">
          <div class="rdb-category-header__icon">
            <v-icon size="20" color="white">mdi-store</v-icon>
          </div>
          <div class="rdb-category-header__text">
            <div class="rdb-category-header__title">ตลาดชุมชน</div>
            <div class="rdb-category-header__sub">สั่งสินค้า / อาหาร จากร้านค้า</div>
          </div>
          <div class="rdb-category-header__count rdb-category-header__count--order">{{ availableOrderJobs.length }} งาน</div>
        </div>

        <div
          v-for="job in availableOrderJobs"
          :key="'job-'+job.id"
          class="rdb-job-card rdb-job-card--order"
        >
          <!-- ── Header: order num + shop name ── -->
          <div class="rdb-job-card__header rdb-job-card__header--order">
            <span class="rdb-job-card__num">#{{ job.orderNumber }}</span>
            <v-spacer></v-spacer>
            <div class="rdb-job-source-badge rdb-job-source-badge--shop">
              <v-icon size="11" color="inherit" class="mr-1">mdi-store</v-icon>
              {{ job.shopName }}
            </div>
            <div class="rdb-job-card__time-ago" v-if="job.timeAgo">
              <v-icon size="11" color="#9CA3AF" class="mr-1">mdi-clock-outline</v-icon>
              {{ job.timeAgo }}ที่แล้ว
            </div>
          </div>

          <!-- ── Route visual ── -->
          <div class="rdb-job-card__body">
            <div class="rdb-job-route-row">
              <div class="rdb-job-route-indicator">
                <div class="rdb-job-route-dot rdb-job-route-dot--pickup"></div>
                <div class="rdb-job-route-connector"></div>
              </div>
              <div class="rdb-job-route-info">
                <div class="rdb-job-route-label">
                  รับสินค้า
                  <span class="rdb-job-dist-chip rdb-job-dist-chip--near" v-if="getJobPickupDistance(job)">
                    <v-icon size="10" color="inherit">mdi-navigation</v-icon>
                    {{ formatDistance(getJobPickupDistance(job)) }} จากคุณ
                  </span>
                </div>
                <div class="rdb-job-route-name">{{ job.shopName }}</div>
                <div class="rdb-job-route-addr">{{ job.pickupAddress }}</div>
              </div>
            </div>
            <div class="rdb-job-route-row">
              <div class="rdb-job-route-indicator">
                <div class="rdb-job-route-dot rdb-job-route-dot--drop"></div>
              </div>
              <div class="rdb-job-route-info">
                <div class="rdb-job-route-label">
                  ส่งที่
                  <span class="rdb-job-dist-chip rdb-job-dist-chip--deliver" v-if="getJobDeliveryDistance(job)">
                    <v-icon size="10" color="inherit">mdi-map-marker-distance</v-icon>
                    {{ formatDistance(getJobDeliveryDistance(job)) }} ระยะเดินทาง
                  </span>
                </div>
                <div class="rdb-job-route-name">{{ job.customerArea || 'ไม่ระบุพื้นที่' }}</div>
              </div>
            </div>
            <div class="rdb-job-summary-strip">
              <div class="rdb-job-summary-item" v-if="getJobPickupDistance(job)">
                <v-icon size="13" color="#3B82F6">mdi-navigation</v-icon>
                <span>{{ formatDistance(getJobPickupDistance(job)) }} ถึงจุดรับ</span>
              </div>
              <div class="rdb-job-summary-item" v-if="getJobDeliveryDistance(job)">
                <v-icon size="13" color="#F4811F">mdi-map-marker-distance</v-icon>
                <span>{{ formatDistance(getJobDeliveryDistance(job)) }} ระยะเดินทาง</span>
              </div>
              <div class="rdb-job-summary-item" v-if="job.itemCount">
                <v-icon size="13" color="#6B7280">mdi-package-variant</v-icon>
                <span>{{ job.itemCount }} รายการ</span>
              </div>
              <div class="rdb-job-summary-item" v-if="getEstimatedTime(job)">
                <v-icon size="13" color="#6B7280">mdi-timer-outline</v-icon>
                <span>ใช้เวลา ~{{ getEstimatedTime(job) }} นาที</span>
              </div>
            </div>
          </div>

          <div class="rdb-job-card__footer">
            <div>
              <div class="rdb-job-card__fee">฿{{ formatNumber(job.deliveryFee) }}</div>
              <div class="rdb-job-fee-per-km" v-if="getTotalDistance(job) && job.deliveryFee">
                ฿{{ getFeePerKm(job) }}/กม.
              </div>
            </div>
            <button
              class="rdb-accept-btn rdb-accept-btn--order"
              @click.stop="acceptJob(job)"
              :disabled="currentDeliveries.length > 0"
            >
              <v-icon size="16" color="white" class="mr-1">mdi-check-circle</v-icon>
              รับงานนี้
            </button>
          </div>
        </div>
      </div>

      <!-- Empty available jobs (online but no jobs) -->
      <div v-if="isOnline && availableJobs.length === 0 && !loading" class="rdb-no-jobs-banner">
        <v-icon size="28" color="#D97706">mdi-magnify</v-icon>
        <div class="rdb-no-jobs-banner__text">
          <div class="rdb-no-jobs-banner__title">กำลังมองหางานใกล้คุณ...</div>
          <div class="rdb-no-jobs-banner__sub">ระบบจะแจ้งเตือนเมื่อมีงานใหม่</div>
        </div>
        <div class="rdb-no-jobs-banner__dot"></div>
      </div>

      <!-- ─── Current Deliveries ─── -->
      <div class="rdb-section">
        <div class="rdb-section__title">
          <span class="rdb-section__dot rdb-section__dot--green"></span>
          งานที่ต้องส่ง
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading" class="rdb-skeleton-card">
          <div class="rdb-skeleton__line rdb-skeleton__line--short"></div>
          <div class="rdb-skeleton__line"></div>
          <div class="rdb-skeleton__line"></div>
        </div>

        <div v-else-if="currentDeliveries.length > 0">
          <div
            v-for="delivery in currentDeliveries"
            :key="(delivery.jobType || 'order') + '-' + delivery.id"
            class="rdb-delivery-card"
            @click="viewDetails(delivery)"
          >
            <!-- Status strip -->
            <div class="rdb-delivery-card__strip" :style="{ background: getStatusGradient(delivery.status, delivery.jobType) }"></div>

            <div class="rdb-delivery-card__content">
              <div class="rdb-delivery-card__top">
                <div class="rdb-delivery-card__status-badge" :style="{ background: getStatusBg(delivery.status, delivery.jobType), color: getStatusColor(delivery.status, delivery.jobType) }">
                  {{ getStatusText(delivery.status, delivery.jobType) }}
                </div>
                <span class="rdb-delivery-card__num">#{{ delivery.orderNumber }}</span>
                <v-spacer></v-spacer>
                <div class="rdb-delivery-card__fee">฿{{ formatNumber(delivery.deliveryFee) }}</div>
              </div>

              <div v-if="delivery.jobType === 'travel'" class="caption amber--text text--darken-2 font-weight-bold mb-1">
                งานเรียกรถ (ผู้โดยสาร)
              </div>

              <!-- Route -->
              <div class="rdb-delivery-card__route">
                <div class="rdb-delivery-card__route-item">
                  <div class="rdb-route-dot rdb-route-dot--from"></div>
                  <div class="rdb-delivery-card__route-text">
                    <div class="rdb-delivery-card__route-label">
                      {{ delivery.jobType === 'travel' ? 'จุดรับผู้โดยสาร' : 'รับสินค้า' }}
                      <span v-if="getJobPickupDistance(delivery)" class="ml-1 text--primary font-weight-bold">
                        ({{ formatDistance(getJobPickupDistance(delivery)) }})
                      </span>
                    </div>
                    <div class="rdb-delivery-card__route-name">{{ delivery.jobType === 'travel' ? delivery.pickupLocation : delivery.sellerName }}</div>
                    <div v-if="delivery.pickupDetail" class="rdb-job-route-note">
                      <v-icon size="11" color="#16a34a" class="mr-1">mdi-note-text-outline</v-icon>
                      {{ delivery.pickupDetail }}
                    </div>
                  </div>
                </div>
                <div class="rdb-delivery-card__route-line"></div>
                <div class="rdb-delivery-card__route-item">
                  <div class="rdb-route-dot rdb-route-dot--to"></div>
                  <div class="rdb-delivery-card__route-text">
                    <div class="rdb-delivery-card__route-label">
                      {{ delivery.jobType === 'travel' ? 'จุดส่งผู้โดยสาร' : 'ส่งที่' }}
                      <span v-if="getJobDeliveryDistance(delivery)" class="ml-1 text--primary font-weight-bold">
                        (ระยะเดินทาง {{ formatDistance(getJobDeliveryDistance(delivery)) }})
                      </span>
                    </div>
                    <div class="rdb-delivery-card__route-name">{{ delivery.jobType === 'travel' ? delivery.deliveryLocation : delivery.customerName }}</div>
                    <div v-if="delivery.deliveryDetail" class="rdb-job-route-note">
                      <v-icon size="11" color="#dc2626" class="mr-1">mdi-note-text-outline</v-icon>
                      {{ delivery.deliveryDetail }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Deadline -->
              <deadline-banner
                :timer-started-at="delivery.timerStartedAt"
                :estimated-duration-minutes="delivery.estimatedDurationMinutes"
                :gross-amount="delivery.deliveryFee"
                :penalty-enabled="delivery.penaltyEnabled !== false"
              />

              <div class="rdb-delivery-card__cta">
                <span class="rdb-delivery-card__tap-hint">แตะเพื่อดูรายละเอียดงาน</span>
                <v-icon size="16" color="#10B981">mdi-chevron-right</v-icon>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="rdb-empty-state">
          <div class="rdb-empty-state__icon">🛵</div>
          <div class="rdb-empty-state__title">ยังไม่มีงานในขณะนี้</div>
          <div class="rdb-empty-state__sub">{{ isOnline ? 'รอรับงานใหม่จากระบบ...' : 'เปิดสถานะออนไลน์เพื่อรับงาน' }}</div>
        </div>
      </div>

      <!-- spacer => bottom nav on mobile -->
      <div style="height: 80px;"></div>
    </div>
  </div>
</template>

<script>
import DeadlineBanner from "@/components/Rider/DeadlineBanner.vue";
import socket from "@/plugins/socket";

export default {
  name: "RiderDashboard",
  components: { DeadlineBanner },

  data() {
    return {
      isOnline: false,
      riderName: "คุณไรเดอร์",
      loading: false,
      stats: {
        todayDeliveries: 0,
        pendingDeliveries: 0,
        todayEarnings: 0,
        balance: 0,
      },
      currentDeliveries: [],
      availableJobs: [],
      riderId: null,
      riderLocation: null,
      pollTimer: null,
    };
  },

  computed: {
    availableTravelJobs() {
      return this.availableJobs.filter(j => j.jobType === 'travel');
    },
    availableOrderJobs() {
      return this.availableJobs.filter(j => j.jobType === 'order');
    },
  },

  mounted() {
    this.fetchDashboardData();

    // ฟังพิกัดล่าสุดจาก Global Layout
    this.$root.$on("rider:location-updated", (loc) => {
      // ถ้าได้รับพิกัดครั้งแรก และกำลังออนไลน์อยู่ ให้โหลดข้อมูลใหม่ (เพราะก่อนหน้านี้ h3Index อาจจะว่าง)
      const isFirstLoc = !this.riderLocation;
      this.riderLocation = loc;

      if (isFirstLoc && this.isOnline) {
        console.log("[RiderDashboard] First location received, re-fetching jobs...");
        this.fetchDashboardData();
      }
    });
    // ร้องขอพิกัดล่าสุดทันที
    this.$root.$emit("rider:request-current-location");

    this.$root.$on("rider:location-error", (msg) => {
      this.$swal.fire("ข้อผิดพลาด", msg, "error");
    });

    // รับงานใหม่ realtime ผ่าน socket
    socket.on("order:nearby:new", this.fetchDashboardData);
    socket.on("travel:nearby:new", this.fetchDashboardData);

    // Polling fallback: ดึงข้อมูลใหม่ทุก 15 วิ กรณี socket miss หรือ availableRiders ว่าง
    this.pollTimer = setInterval(this.fetchDashboardData, 15000);
  },

  beforeDestroy() {
    this.$root.$off("rider:location-updated");
    this.$root.$off("rider:location-error");
    socket.off("order:nearby:new", this.fetchDashboardData);
    socket.off("travel:nearby:new", this.fetchDashboardData);
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },

  methods: {
    calculateDistance(lat1, lon1, lat2, lon2) {
      if (!lat1 || !lon1 || !lat2 || !lon2) return null;
      const R = 6371; // Radius of the earth in km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c; // Distance in km
    },

    formatDistance(dist) {
      if (dist === null || dist === undefined) return '...';
      if (dist < 1) return `${(dist * 1000).toFixed(0)} ม.`;
      return `${dist.toFixed(1)} กม.`;
    },

    getJobPickupDistance(job) {
      // ── rider → pickup point (H3-decoded coords are good enough for this) ──
      if (!this.riderLocation || !job.shopLat || !job.shopLng) return null;
      return this.calculateDistance(this.riderLocation.lat, this.riderLocation.lng, job.shopLat, job.shopLng);
    },

    getJobDeliveryDistance(job) {
      // ── For travel: ALWAYS use distanceKm from DB (Google Maps value set by user) ──
      // H3 cell centers are only ~100-300m accurate → gives wrong distance
      if (job.jobType === 'travel') {
        return job.distanceKm || null;
      }
      // ── For orders: use real GPS coords from seller/buyer addresses ──
      if (!job.shopLat || !job.shopLng || !job.customerLat || !job.customerLng) return null;
      return this.calculateDistance(job.shopLat, job.shopLng, job.customerLat, job.customerLng);
    },

    async fetchDashboardData() {
      this.loading = true;
      try {
        const response = await this.$axios.get(`${this.$domain_api}/api/v1/auth/rider/dashboard`, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        });

        if (response.data.success) {
          const { rider, stats, currentDeliveries, currentTravels, availableOrders } = response.data.data;
          this.riderName = rider.username || "คุณไรเดอร์";
          this.isOnline = rider.isOnline;
          this.riderId = rider.id;
          this.stats = stats;

          const orderJobs = (availableOrders || []).map(order => ({
            id: order.id,
            jobType: 'order',
            orderNumber: order.orderNumber,
            sourceType: 'shop',
            shopName: order.seller?.shopName || "ไม่ระบุ",
            pickupAddress: order.seller?.address?.detail || "ไม่ระบุตำแหน่ง",
            customerArea: (() => {
              const sd = order.address?.subDistrict;
              if (!sd) return null;
              return [sd.nameTh, sd.district?.nameTh].filter(Boolean).join(', ');
            })(),
            deliveryFee: order.shipFee || 0,
            itemCount: order.orderItems?.length || order.itemCount || null,
            createdAt: order.createdAt || null,
            timeAgo: order.createdAt ? this.getTimeAgo(order.createdAt) : null,
            shopLat: order.seller?.address?.lat,
            shopLng: order.seller?.address?.lng,
            customerLat: order.address?.lat,
            customerLng: order.address?.lng,
          }));

          // ดึง travel ที่รอ rider ใกล้เคียง
          let travelJobs = [];
          try {
            const travelRes = await this.$axios.get(`${this.$domain_api}/api/v1/travels/available`, {
              headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
            });
            travelJobs = (travelRes.data?.data || []).map(t => ({
              id: t.id,
              jobType: 'travel',
              orderNumber: t.travelNumber || `TRIP-${String(t.id).slice(-6).toUpperCase()}`,
              sourceType: 'user',
              shopName: `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.trim() || "ผู้โดยสาร",
              pickupAddress: t.addressSnapshotNow || "ไม่ระบุจุดรับ",
              customerArea: t.addressSnapshotStart || "ไม่ระบุจุดส่ง",
              pickupDetail: t.originDetail || "",
              deliveryDetail: t.destinationDetail || "",
              note: t.note || "",
              deliveryFee: t.price || 0,
              addonTotal: t.addonTotal || 0,
              selectedAddons: t.selectedAddons || [],
              itemCount: null,
              createdAt: t.createdAt || null,
              timeAgo: t.createdAt ? this.getTimeAgo(t.createdAt) : null,
              distanceKm: t.distance || null,
              tripDurationMinutes: t.tripDurationMinutes || null,
              shopLat: t.latNow != null ? Number(t.latNow) : null,
              shopLng: t.lngNow != null ? Number(t.lngNow) : null,
              customerLat: t.latDest != null ? Number(t.latDest) : null,
              customerLng: t.lngDest != null ? Number(t.lngDest) : null,
            }));
          } catch (e) {
            console.warn("[RiderDashboard] travels/available failed:", e?.response?.data || e?.message || e);
          }


          this.availableJobs = [...orderJobs, ...travelJobs].sort((a, b) => {
            const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return tb - ta;
          });

          if (this.isOnline) {
            this.$root.$emit("rider:update-online-status", true);
          }

          const orderDeliveries = (currentDeliveries || []).map((order) => ({
            id: order.id,
            jobType: "order",
            orderNumber: order.orderNumber,
            status: order.status,
            pickupLocation: order.seller?.address?.detail || "ไม่ระบุตำแหน่ง",
            sellerName: order.seller?.shopName || "ไม่ระบุร้านค้า",
            deliveryLocation: order.address?.detail || "ไม่ระบุตำแหน่ง",
            customerName:
              `${order.buyer?.firstName || ""} ${order.buyer?.lastName || ""}`.trim() ||
              order.address?.firstName ||
              "ลูกค้า",
            customerPhone: order.address?.phone || order.buyer?.phone || "ไม่ระบุเบอร์โทร",
            deliveryFee: order.shipFee || 0,
            shopLat: order.seller?.address?.lat,
            shopLng: order.seller?.address?.lng,
            customerLat: order.address?.lat,
            customerLng: order.address?.lng,
            timerStartedAt: order.timerStartedAt || null,
            estimatedDurationMinutes: order.estimatedDurationMinutes || null,
            penaltyEnabled: order.penaltyEnabled !== false,
          }));

          const travelDeliveries = (currentTravels || []).map((t) => ({
            id: t.id,
            jobType: "travel",
            orderNumber: t.travelNumber || `TRIP-${t.id.slice(-6).toUpperCase()}`,
            status: t.status,
            pickupLocation: t.addressSnapshotNow || "—",
            sellerName: "",
            deliveryLocation: t.addressSnapshotStart || "—",
            pickupDetail: t.originDetail || "",
            deliveryDetail: t.destinationDetail || "",
            customerName:
              [t.user?.firstName, t.user?.lastName].filter(Boolean).join(" ").trim() || "ผู้โดยสาร",
            customerPhone: t.user?.defaultAddress?.phone || "ไม่ระบุเบอร์โทร",
            deliveryFee: t.price || 0,
            distanceKm: t.distance || null,
            shopLat: t.pickupLat != null ? Number(t.pickupLat) : null,
            shopLng: t.pickupLng != null ? Number(t.pickupLng) : null,
            customerLat: t.dropLat != null ? Number(t.dropLat) : null,
            customerLng: t.dropLng != null ? Number(t.dropLng) : null,
            timerStartedAt: t.timerStartedAt || null,
            estimatedDurationMinutes: t.estimatedDurationMinutes || null,
            penaltyEnabled: t.penaltyEnabled !== false,
          }));


          this.currentDeliveries = [...orderDeliveries, ...travelDeliveries];
        }
      } catch (error) {
        console.error("Fetch dashboard error:", error);
        this.$swal.fire({ icon: 'error', title: 'ข้อผิดพลาด', text: 'ไม่สามารถดึงข้อมูลแดชบอร์ดได้' });
      } finally {
        this.loading = false;
      }
    },

    async toggleOnlineStatus() {
      const newStatus = !this.isOnline;
      let lat, lng;

      if (newStatus && navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
          });
          lat = position.coords.latitude;
          lng = position.coords.longitude;
        } catch (err) {
          console.warn("Could not get initial position:", err);
        }
      }

      try {
        const payload = { isOnline: newStatus };
        if (lat !== undefined && lng !== undefined) { payload.lat = lat; payload.lng = lng; }

        const response = await this.$axios.post(`${this.$domain_api}/api/v1/auth/rider/toggle-online`,
          payload,
          { headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` } }
        );

        if (response.data.success) {
          this.isOnline = newStatus;
          this.$root.$emit("rider:update-online-status", newStatus);
          this.$swal.fire({ icon: 'success', title: 'สำเร็จ', text: response.data.message, timer: 1500, showConfirmButton: false });
          // รีโหลดข้อมูลงานทันทีหลังจากออนไลน์
          this.fetchDashboardData();
        }
      } catch (error) {
        this.$swal.fire({ icon: 'error', title: 'ข้อผิดพลาด', text: error.response?.data?.message || 'ไม่สามารถเปลี่ยนสถานะได้' });
      }
    },

    formatNumber(num) {
      return new Intl.NumberFormat("th-TH").format(num);
    },

    getStatusGradient(status, jobType) {
      if (jobType === "travel") {
        const g = {
          rider_accepted: "linear-gradient(180deg, #2563EB, #1D4ED8)",
          in_progress: "linear-gradient(180deg, #7C3AED, #5B21B6)",
        };
        return g[status] || "linear-gradient(180deg, #9CA3AF, #6B7280)";
      }
      const g = {
        processing: "linear-gradient(180deg, #F4811F, #E56A0A)",
        picked_up: "linear-gradient(180deg, #6366F1, #4F46E5)",
        delivering: "linear-gradient(180deg, #6366F1, #4F46E5)",
        delivered: "linear-gradient(180deg, #10B981, #059669)",
      };
      return g[status] || "linear-gradient(180deg, #9CA3AF, #6B7280)";
    },

    getStatusBg(status, jobType) {
      if (jobType === "travel") {
        const m = { rider_accepted: "#EFF6FF", in_progress: "#F5F3FF" };
        return m[status] || "#F3F4F6";
      }
      const m = { processing: "#FFF7ED", picked_up: "#EEF2FF", delivering: "#EEF2FF", delivered: "#ECFDF5" };
      return m[status] || "#F3F4F6";
    },

    getStatusColor(status, jobType) {
      if (jobType === "travel") {
        const m = { rider_accepted: "#1D4ED8", in_progress: "#5B21B6" };
        return m[status] || "#374151";
      }
      const m = { processing: "#C2410C", picked_up: "#4338CA", delivering: "#4338CA", delivered: "#065F46" };
      return m[status] || "#374151";
    },

    getStatusText(status, jobType) {
      if (jobType === "travel") {
        return (
          {
            rider_accepted: "ไปรับผู้โดยสาร",
            in_progress: "กำลังเดินทาง",
            completed: "สำเร็จ",
          }[status] || status
        );
      }
      return (
        {
          pending: "รอรับงาน",
          accepted: "รับงานแล้ว",
          processing: "รอรับสินค้า",
          picked_up: "รับสินค้าแล้ว",
          delivering: "กำลังจัดส่ง",
          delivered: "จัดส่งแล้ว",
          cancelled: "ยกเลิก",
        }[status] || status
      );
    },

    getTotalDistance(job) {
      const pickup  = this.getJobPickupDistance(job);
      const deliver = this.getJobDeliveryDistance(job);
      if (pickup === null && deliver === null) return null;
      return (pickup || 0) + (deliver || 0);
    },

    getEstimatedTime(job) {
      // travel: ใช้ Google Maps duration ที่เก็บตอนสร้าง travel (pickup→dest)
      if (job.jobType === 'travel') {
        if (job.tripDurationMinutes) return job.tripDurationMinutes;
        // fallback: ใช้ distanceKm / 20 km/h ถ้า Google Maps ไม่มี
        if (job.distanceKm) return Math.max(1, Math.round((job.distanceKm / 20) * 60));
        return null;
      }
      // order: ยังไม่มี Google Maps pre-calc → ประมาณจาก Haversine × 1.4 / 20 km/h
      const pickup  = this.getJobPickupDistance(job);
      const deliver = this.getJobDeliveryDistance(job);
      if (pickup === null && deliver === null) return null;
      const totalKm = ((pickup || 0) + (deliver || 0)) * 1.4;
      if (!totalKm) return null;
      return Math.max(1, Math.round((totalKm / 20) * 60));
    },

    getFeePerKm(job) {
      const total = this.getTotalDistance(job);
      if (!total || total === 0) return '—';
      return (job.deliveryFee / total).toFixed(1);
    },

    getTimeAgo(dateStr) {
      if (!dateStr) return null;
      const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
      if (diff < 60) return `${diff} วิ`;
      if (diff < 3600) return `${Math.floor(diff / 60)} นาที`;
      return `${Math.floor(diff / 3600)} ชม.`;
    },

    async acceptJob(job) {
      if (!this.isOnline) {
        this.$swal.fire('ข้อผิดพลาด', 'กรุณาเปิดสถานะเป็น "ออนไลน์" เพื่อรับงาน', 'error');
        return;
      }
      if (this.currentDeliveries.length > 0) {
        this.$swal.fire('คุณมีงานที่ยังไม่เสร็จ', 'จัดส่งออเดอร์ปัจจุบันให้สำเร็จก่อนรับงานใหม่', 'warning');
        return;
      }
      try {
        const endpoint = job.jobType === 'travel'
          ? `${this.$domain_api}/api/v1/travels/${job.id}/rider-accept`
          : `${this.$domain_api}/api/v1/orders/${job.id}/rider-accept`;

        const response = await this.$axios.post(endpoint, {}, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        });

        if (response.data.success) {
          const msg = job.jobType === 'travel' ? 'ไปรับผู้โดยสารได้เลย!' : 'ไปรับสินค้าที่ร้านค้าได้เลย!';
          this.$swal.fire({ icon: 'success', title: 'รับงานสำเร็จ', text: msg, timer: 2000, showConfirmButton: false });
          this.fetchDashboardData();
        }
      } catch (error) {
        this.$swal.fire({ icon: 'error', title: 'ข้อผิดพลาด', text: error.response?.data?.message || 'ไม่สามารถรับงานได้' });
      }
    },

    viewDetails(item) {
      if (item.jobType === "travel") {
        this.$router.push({ name: "rider_travel_detail", params: { id: item.id } });
      } else {
        this.$router.push({ name: "rider_order_detail", params: { id: item.id } });
      }
    },
  },
};
</script>

<style scoped>
.rdb-page {
  min-height: 100vh;
  background: #F1F5F9;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  overflow-x: hidden;
}

/* ── Hero ── */
.rdb-hero {
  padding: 20px 20px 0;
  border-radius: 0 0 32px 32px;
  transition: background 0.5s;
}

.rdb-hero--online {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
}

.rdb-hero--offline {
  background: linear-gradient(135deg, #6B7280 0%, #4B5563 100%);
}

.rdb-hero__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.rdb-hero__greeting {
  font-size: 13px;
  color: rgba(255,255,255,0.75);
  font-weight: 500;
}

.rdb-hero__name {
  font-size: 22px;
  font-weight: 800;
  color: white;
  letter-spacing: -0.5px;
}

/* Online toggle button */
.rdb-status-toggle {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.rdb-status-toggle--on {
  background: rgba(255,255,255,0.25);
  color: white;
  backdrop-filter: blur(4px);
  border: 1.5px solid rgba(255,255,255,0.4);
}

.rdb-status-toggle--off {
  background: rgba(0,0,0,0.2);
  color: rgba(255,255,255,0.8);
  border: 1.5px solid rgba(255,255,255,0.15);
}

.rdb-status-toggle:active {
  transform: scale(0.95);
  opacity: 0.85;
}

.rdb-status-toggle__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  animation: rdb-pulse 1.5s infinite;
}

.rdb-status-toggle--off .rdb-status-toggle__dot {
  animation: none;
  opacity: 0.5;
}

@keyframes rdb-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.8); }
}

/* Stats in hero */
.rdb-hero__stats {
  display: flex;
  align-items: center;
  background: rgba(0,0,0,0.12);
  border-radius: 20px 20px 0 0;
  padding: 16px 20px;
  gap: 0;
}

.rdb-hero__stat {
  flex: 1;
  text-align: center;
}

.rdb-hero__stat-val {
  font-size: 18px;
  font-weight: 800;
  color: white;
}

.rdb-hero__stat-lbl {
  font-size: 11px;
  color: rgba(255,255,255,0.7);
  margin-top: 2px;
  font-weight: 500;
}

.rdb-hero__stat-divider {
  width: 1px;
  height: 32px;
  background: rgba(255,255,255,0.2);
}

/* ── Body ── */
.rdb-body {
  padding: 16px;
}

/* ── Active Banner ── */
.rdb-active-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
  border-radius: 18px;
  padding: 14px 16px;
  margin-bottom: 20px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.35);
  transition: transform 0.15s, opacity 0.15s;
}

.rdb-active-banner:active { transform: scale(0.98); opacity: 0.9; }

.rdb-active-banner__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rdb-active-banner__pulse {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: rdb-pulse 1.5s infinite;
}

.rdb-active-banner__title {
  font-size: 14px;
  font-weight: 800;
  color: white;
}

.rdb-active-banner__sub {
  font-size: 12px;
  color: rgba(255,255,255,0.75);
  margin-top: 2px;
}

/* ── Section ── */
.rdb-section {
  margin-bottom: 24px;
}

.rdb-section__title {
  font-size: 12px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rdb-section__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.rdb-section__dot--amber { background: #F59E0B; }
.rdb-section__dot--green { background: #10B981; }

/* ── Section header ── */
.rdb-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.rdb-section__header .rdb-section__title {
  margin-bottom: 0;
}

.rdb-section__count {
  font-size: 11px;
  font-weight: 700;
  background: #FEF3C7;
  color: #92400E;
  padding: 2px 10px;
  border-radius: 20px;
}

/* ── Job Card (Available) ── */
.rdb-job-card {
  background: white;
  border-radius: 18px;
  overflow: hidden;
  margin-bottom: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  transition: transform 0.15s, box-shadow 0.15s;
}

.rdb-job-card--order {
  border: 1.5px solid #FDE68A;
}

.rdb-job-card--travel {
  border: 1.5px solid #BFDBFE;
}

.rdb-job-card:active {
  transform: scale(0.98);
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}

/* ── Category section header ── */
.rdb-category-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  margin-bottom: 12px;
}

.rdb-category-header--travel {
  background: linear-gradient(135deg, #2563EB, #1D4ED8);
}

.rdb-category-header--order {
  background: linear-gradient(135deg, #F59E0B, #D97706);
}

.rdb-category-header__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rdb-category-header__text {
  flex: 1;
}

.rdb-category-header__title {
  font-size: 16px;
  font-weight: 800;
  color: white;
  line-height: 1.2;
}

.rdb-category-header__sub {
  font-size: 12px;
  color: rgba(255,255,255,0.8);
  font-weight: 500;
  margin-top: 2px;
}

.rdb-category-header__count {
  font-size: 11px;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 20px;
  flex-shrink: 0;
}

.rdb-category-header__count--travel {
  background: rgba(255,255,255,0.2);
  color: white;
}

.rdb-category-header__count--order {
  background: rgba(255,255,255,0.2);
  color: white;
}

/* Card header */
.rdb-job-card__header {
  padding: 6px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #F3F4F6;
}

.rdb-job-card__header--travel {
  background: #EFF6FF;
}

.rdb-job-card__header--order {
  background: #FFFBEB;
}

/* Source type badge */
.rdb-job-source-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rdb-job-source-badge--shop {
  background: #FEF3C7;
  color: #92400E;
}

.rdb-job-source-badge--user {
  background: #EEF2FF;
  color: #3730A3;
}

.rdb-job-card__num {
  font-size: 12px;
  font-weight: 700;
  color: #92400E;
}

.rdb-job-card__time-ago {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #9CA3AF;
  font-weight: 600;
}

/* Body */
.rdb-job-card__body {
  padding: 14px 14px 8px;
}

/* Route rows */
.rdb-job-route-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 2px;
}

.rdb-job-route-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 4px;
  width: 12px;
  flex-shrink: 0;
}

.rdb-job-route-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid white;
}

.rdb-job-route-dot--pickup {
  background: #3B82F6;
  box-shadow: 0 0 0 2px #3B82F6;
}

.rdb-job-route-dot--drop {
  background: #F4811F;
  box-shadow: 0 0 0 2px #F4811F;
}

.rdb-job-route-connector {
  width: 2px;
  height: 28px;
  background: linear-gradient(#3B82F6, #F4811F);
  margin-top: 3px;
}

.rdb-job-route-info {
  flex: 1;
  min-width: 0;
  padding-bottom: 10px;
}

.rdb-job-route-label {
  font-size: 10px;
  color: #9CA3AF;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 1px;
}

.rdb-job-route-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rdb-job-route-addr {
  font-size: 11px;
  color: #9CA3AF;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.rdb-job-route-note {
  display: flex;
  align-items: flex-start;
  margin-top: 3px;
  padding: 4px 8px;
  background: #fffbeb;
  border-left: 2px solid #f59e0b;
  border-radius: 4px;
  font-size: 11px;
  color: #78350f;
  word-break: break-word;
  line-height: 1.4;
}

.rdb-job-rider-note {
  display: flex;
  align-items: flex-start;
  margin: 6px 0 4px;
  padding: 6px 10px;
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  font-size: 12px;
  color: #92400E;
  font-weight: 500;
  word-break: break-word;
  line-height: 1.4;
}

/* Distance chips */
.rdb-job-dist-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
}

.rdb-job-dist-chip--near {
  background: #DBEAFE;
  color: #1D4ED8;
}

.rdb-job-dist-chip--deliver {
  background: #FFF3E0;
  color: #C2410C;
}

/* Summary strip */
.rdb-job-summary-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #F9FAFB;
  border-radius: 10px;
  padding: 7px 10px;
  margin-top: 4px;
}

.rdb-job-summary-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6B7280;
  font-weight: 600;
}

/* Footer */
.rdb-job-card__footer {
  padding: 10px 14px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rdb-job-card__fee {
  font-size: 24px;
  font-weight: 900;
  color: #10B981;
  line-height: 1;
}

.rdb-job-fee-per-km {
  font-size: 11px;
  color: #9CA3AF;
  font-weight: 600;
  margin-top: 2px;
}

.rdb-job-fee-addon {
  font-size: 11px;
  color: #7C3AED;
  font-weight: 700;
  margin-top: 1px;
}

/* Add-ons block */
.rdb-job-addons {
  margin: 6px 0 4px;
  padding: 8px 10px;
  background: #F5F3FF;
  border: 1px solid #DDD6FE;
  border-radius: 8px;
}

.rdb-job-addons__title {
  font-size: 10px;
  font-weight: 700;
  color: #6D28D9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.rdb-job-addon-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 0;
  border-bottom: 1px dashed #EDE9FE;
}

.rdb-job-addon-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.rdb-job-addon-name {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.rdb-job-addon-price {
  font-size: 12px;
  font-weight: 800;
  color: #059669;
  white-space: nowrap;
  margin-left: 8px;
}

/* No jobs banner */
.rdb-no-jobs-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #FFFBEB;
  border: 1.5px dashed #FDE68A;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;
}

.rdb-no-jobs-banner__text { flex: 1; }

.rdb-no-jobs-banner__title {
  font-size: 13px;
  font-weight: 700;
  color: #92400E;
}

.rdb-no-jobs-banner__sub {
  font-size: 11px;
  color: #B45309;
  margin-top: 2px;
}

.rdb-no-jobs-banner__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #F59E0B;
  animation: rdb-pulse 1.5s infinite;
}

.rdb-accept-btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 18px;
  background: linear-gradient(135deg, #3B82F6, #2563EB);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 800;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
}

.rdb-accept-btn--order {
  background: linear-gradient(135deg, #F59E0B, #D97706);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
}

.rdb-accept-btn:active { transform: scale(0.95); opacity: 0.85; }
.rdb-accept-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Delivery Card (Active) ── */
.rdb-delivery-card {
  background: white;
  border-radius: 18px;
  overflow: hidden;
  margin-bottom: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.07);
  display: flex;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.rdb-delivery-card:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.rdb-delivery-card__strip {
  width: 5px;
  flex-shrink: 0;
}

.rdb-delivery-card__content {
  flex: 1;
  padding: 14px 16px;
  min-width: 0;
}

.rdb-delivery-card__top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.rdb-delivery-card__status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.rdb-delivery-card__num {
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 600;
}

.rdb-delivery-card__fee {
  font-size: 16px;
  font-weight: 800;
  color: #10B981;
  white-space: nowrap;
}

/* Route visualization */
.rdb-delivery-card__route {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
}

.rdb-delivery-card__route-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rdb-route-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid white;
  box-shadow: 0 0 0 2px currentColor;
}

.rdb-route-dot--from {
  background: #3B82F6;
  color: #3B82F6;
}

.rdb-route-dot--to {
  background: #F4811F;
  color: #F4811F;
}

.rdb-delivery-card__route-line {
  width: 2px;
  height: 12px;
  background: linear-gradient(#3B82F6, #F4811F);
  margin-left: 4px;
}

.rdb-delivery-card__route-text { flex: 1; min-width: 0; }

.rdb-delivery-card__route-label {
  font-size: 10px;
  color: #9CA3AF;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rdb-delivery-card__route-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rdb-delivery-card__cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid #F3F4F6;
}

.rdb-delivery-card__tap-hint {
  font-size: 11px;
  color: #10B981;
  font-weight: 600;
}

/* ── Skeleton ── */
.rdb-skeleton-card {
  background: white;
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.rdb-skeleton__line {
  height: 14px;
  background: linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%);
  background-size: 200% 100%;
  animation: rdb-shimmer 1.2s infinite;
  border-radius: 7px;
  margin-bottom: 10px;
}

.rdb-skeleton__line--short { width: 40%; }

@keyframes rdb-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Service Card ── */
.rdb-service-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  border: 1.5px solid #FEE2E2;
}

.rdb-service-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #FFFBEB;
  border-bottom: 1px solid #FEF3C7;
}

.rdb-service-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #FEF3ED;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.rdb-service-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.rdb-service-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.rdb-service-chip {
  font-size: 10px;
  font-weight: 700;
  background: #DBEAFE;
  color: #1D4ED8;
  padding: 2px 8px;
  border-radius: 20px;
}

.rdb-service-chip--gray {
  background: #F3F4F6;
  color: #6B7280;
}

.rdb-service-card__addons {
  padding: 10px 16px 14px;
}

.rdb-service-card__addon-title {
  font-size: 10px;
  font-weight: 700;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
}

.rdb-service-addon-row {
  display: flex;
  align-items: center;
  padding: 7px 10px;
  background: #FFF7ED;
  border-radius: 8px;
  margin-bottom: 6px;
  border-left: 3px solid #F4811F;
}

.rdb-service-addon-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  min-width: 0;
}

.rdb-service-addon-price {
  font-size: 13px;
  font-weight: 800;
  color: #059669;
  white-space: nowrap;
  margin-left: 8px;
}

.rdb-service-card__no-addon {
  padding: 10px 16px 14px;
  font-size: 12px;
  color: #9CA3AF;
}

/* ── Empty State ── */
.rdb-empty-state {
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.rdb-empty-state__icon {
  font-size: 52px;
  margin-bottom: 12px;
}

.rdb-empty-state__title {
  font-size: 16px;
  font-weight: 700;
  color: #374151;
}

.rdb-empty-state__sub {
  font-size: 13px;
  color: #9CA3AF;
  margin-top: 4px;
}
</style>
